import { sendApiRequest, handle400Statuses } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';

// Turning a pasted list of tickers and ids into the numeric company_ids /feed/view actually takes.
//
// The endpoint accepts company_ids ONLY — no ticker support — and the sole lookup available is
// `v1/genai_tickers_search?query=X`, which is fuzzy, one symbol per request, and returns up to 30
// candidates. So a pasted list costs one request per symbol, and every response needs a decision
// about whether it really matched.
//
// Three rules follow, and each is here to stop a wrong company entering the filter unnoticed —
// which would skew the whole view with nothing on screen to explain it:
//
//   1. EXACT SYMBOL ONLY. A candidate counts when its symbol equals the token, case-insensitively,
//      and it carries a company_id. `APPL` does not quietly become Apple.
//   2. Anything else comes back UNRESOLVED, carrying the candidates that call already returned, so
//      the caller can offer them as one-click picks without spending another request.
//   3. Nothing retries. `useTagSearchResults` re-fires on any non-200 every 500ms with no ceiling;
//      that is survivable for one typeahead and a flood at fan-out scale, so this calls the
//      endpoint directly and treats a failure as unresolved.

export const MAX_ENTRIES = 50;
const CONCURRENCY = 6;

// Splits on the separators a spreadsheet column or a hand-typed list actually produces, then
// strips the decoration that comes with them: surrounding quotes from a CSV export, and the `$`
// prefix people type out of habit. A stray header like `Ticker` is not special-cased — it simply
// fails to resolve and lands in front of the analyst.
export const parseEntries = (text) =>
  String(text || '')
    .split(/[\s,;]+/)
    .map((raw) =>
      raw
        .trim()
        .replace(/^["']+|["']+$/g, '')
        .replace(/^\$/, '')
    )
    .filter(Boolean);

// Drop one entry from a textarea's worth of them and give back the text that remains.
//
// Here rather than in the component that needed it: taking a token OUT of a list has to agree with
// how the list was read IN, and the caller had re-typed the split, the quote strip and the `$` strip
// inline to do it. Two spellings of one parse is how the two stop agreeing — a token the parser
// accepts but the remover misses never leaves the box, and the analyst re-resolves it forever.
//
// Matching is case-insensitive because the caller compares a typed token against a resolved symbol,
// and nobody types `aapl` expecting it to survive. Re-joined with ', ' rather than preserving the
// original separators: the text is round-tripped through parseEntries, so whatever the analyst
// pasted — tabs, newlines, semicolons — comes back as one canonical list. That is a visible change
// to the box mid-typing and it is the intended one; the alternative is a surgical splice that has to
// re-derive the same parse a third time.
export const removeEntry = (text, entry) => {
  const target = String(entry || '').toUpperCase();
  return parseEntries(text)
    .filter((token) => token.toUpperCase() !== target)
    .join(', ');
};

const isId = (entry) => /^\d+$/.test(entry);

const searchSymbol = async (symbol) => {
  const url = `v1/genai_tickers_search?query=${encodeURIComponent(symbol)}`;
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);
  if (status !== 200 || !Array.isArray(data)) return null;
  return data;
};

// A doc-tag-only result has no company_id and cannot be sent to this endpoint.
const companyCandidates = (results) =>
  (results || []).filter((r) => r && r.company_id !== undefined && r.company_id !== null);

const exactMatch = (candidates, symbol) =>
  candidates.find((c) => String(c.symbol || '').toUpperCase() === symbol.toUpperCase()) || null;

// Runs `worker` over `items` with at most `limit` in flight, preserving input order in the result.
const mapWithLimit = async (items, limit, worker) => {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
};

// Resolves pasted text against the companies already in the filter.
//
// Returns { added, duplicates, unresolved, dropped, remainingText } where `added` is ready to
// append to the filter, `unresolved` carries each failed token plus its candidates, and
// `remainingText` is what should stay in the textarea — the failures, so they can be fixed in
// place rather than retyped.
export const resolveCompanies = async (text, existing = [], options = {}) => {
  const onProgress = options.onProgress || (() => {});
  const entries = parseEntries(text);

  const kept = entries.slice(0, MAX_ENTRIES);
  const dropped = entries.length - kept.length;

  const haveId = new Set((existing || []).map((c) => Number(c.company_id)));
  const added = [];
  const unresolved = [];
  let duplicates = 0;
  let completed = 0;

  // De-duplicate within the paste itself before spending a request on the same symbol twice.
  const seen = new Set();
  const unique = kept.filter((entry) => {
    const key = entry.toUpperCase();
    if (seen.has(key)) {
      duplicates += 1;
      return false;
    }
    seen.add(key);
    return true;
  });

  const ids = unique.filter(isId);
  const symbols = unique.filter((entry) => !isId(entry));

  ids.forEach((entry) => {
    const companyId = Number(entry);
    if (haveId.has(companyId)) {
      duplicates += 1;
      return;
    }
    haveId.add(companyId);
    // Nothing client-side can turn an id into a name, so it is carried unverified and the chip
    // says so — an id typo must not look as trustworthy as a resolved ticker.
    added.push({ company_id: companyId, symbol: null, name: null, unverified: true });
  });

  completed += ids.length;
  onProgress(completed, unique.length);

  const searched = await mapWithLimit(symbols, CONCURRENCY, async (symbol) => {
    const results = await searchSymbol(symbol);
    completed += 1;
    onProgress(completed, unique.length);
    return { symbol, results };
  });

  searched.forEach(({ symbol, results }) => {
    if (results === null) {
      unresolved.push({ entry: symbol, reason: 'lookup-failed', candidates: [] });
      return;
    }
    const candidates = companyCandidates(results);
    const match = exactMatch(candidates, symbol);
    if (!match) {
      unresolved.push({
        entry: symbol,
        reason: candidates.length ? 'no-exact-match' : 'not-found',
        candidates: candidates.slice(0, 4),
      });
      return;
    }
    if (haveId.has(Number(match.company_id))) {
      duplicates += 1;
      return;
    }
    haveId.add(Number(match.company_id));
    added.push({ company_id: match.company_id, symbol: match.symbol, name: match.name });
  });

  return {
    added,
    duplicates,
    unresolved,
    dropped,
    remainingText: unresolved.map((u) => u.entry).join(', '),
  };
};

// The line under the button after a resolve. Silence would leave "nothing happened" and "12 of
// your 15 went in" looking identical.
export const summarizeResolution = ({ added, duplicates, unresolved, dropped }) => {
  const parts = [];
  if (added.length) parts.push(`Added ${added.length}.`);
  if (duplicates) parts.push(`${duplicates} already in the filter.`);
  if (unresolved.length) parts.push(`${unresolved.length} unresolved.`);
  if (dropped) parts.push(`${dropped} over the ${MAX_ENTRIES}-entry limit ignored.`);
  if (parts.length === 0) return 'Nothing to add.';
  return parts.join(' ');
};
